// src/features/calls/providers/googleMeetProvider.ts
// Google Meet join URL provider for AX1 Calls orchestration
// Uses HYBRID strategy: workspace default URL or meet.new fallback

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface GoogleMeetConfig {
  /** Workspace-configured default Meet URL (stable room) */
  defaultMeetUrl?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lightweight URL validation without heavy dependencies
 */
function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

/** Fallback URL for instant meetings (requires user to be signed into Google) */
const MEET_NEW_URL = 'https://meet.new';

/**
 * Resolves the Google Meet join URL using hybrid strategy:
 * 1. If workspace has a configured default URL, use it (stable)
 * 2. Otherwise fallback to meet.new (instant meeting)
 * 
 * @param config - Configuration containing optional workspace default URL
 * @returns Valid Google Meet join URL
 */
export function resolveGoogleMeetJoinUrl(config: GoogleMeetConfig): string {
  const { defaultMeetUrl } = config;
  
  // Check if workspace has a valid default URL configured
  if (defaultMeetUrl) {
    const trimmed = defaultMeetUrl.trim();
    if (trimmed.length > 0 && isValidUrl(trimmed)) {
      return trimmed;
    }
  }
  
  // Fallback to meet.new for instant meetings
  return MEET_NEW_URL;
}

/**
 * Check if the resolved URL is using the fallback (meet.new)
 * Useful for showing tooltip about setting a default URL
 */
export function isUsingFallbackMeetUrl(config: GoogleMeetConfig): boolean {
  return resolveGoogleMeetJoinUrl(config) === MEET_NEW_URL;
}

/**
 * Provider name for display purposes
 */
export const PROVIDER_NAME = 'google_meet';

/**
 * Get display-friendly provider label
 */
export function getProviderLabel(): string {
  return 'Google Meet';
}
